import { useState, useEffect } from "react";
import { serverBaseUrl } from "../constants/Constants";
import { DEFAULT_VOICE_CREDIT_PLANS } from "../constants/voiceCreditPlans";

function planRowKey(p) {
  return `${p.name}|${p.amountInRupees}|${p.tokenCredits}`;
}

function plansMatchApiAndFallback(apiPlans, fallbackPlans) {
  if (apiPlans.length !== fallbackPlans.length) return false;
  const fb = new Set(fallbackPlans.map(planRowKey));
  return apiPlans.every((p) => fb.has(planRowKey(p)));
}

function mapServerPlans(plans) {
  if (!Array.isArray(plans)) return null;
  const mapped = plans
    .map((p) => ({
      name: String(p.name || "").trim(),
      amountInRupees: Number(p.amountInRupees),
      tokenCredits: Number(p.creditTokens),
    }))
    .filter(
      (row) =>
        row.name &&
        Number.isFinite(row.amountInRupees) &&
        row.amountInRupees > 0 &&
        Number.isFinite(row.tokenCredits) &&
        row.tokenCredits > 0
    );
  return mapped.length ? mapped : null;
}

/**
 * Loads canonical payment plans from the API (public GET).
 * @param {boolean} enabled When false, skips fetch (e.g. closed modal).
 */
export function useVoiceCreditPlans(enabled = true) {
  const [plans, setPlans] = useState(DEFAULT_VOICE_CREDIT_PLANS);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${serverBaseUrl}/orders/plans`, {
          method: "GET",
          headers: { Accept: "application/json" },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!json?.success || !json?.data?.plans) throw new Error("Invalid plans response");
        const mapped = mapServerPlans(json.data.plans);
        if (mapped && !cancelled) {
          if (
            process.env.NODE_ENV === "development" &&
            !plansMatchApiAndFallback(mapped, DEFAULT_VOICE_CREDIT_PLANS)
          ) {
            console.warn(
              "[useVoiceCreditPlans] API plans differ from paymentPlans.canonical.json — update server JSON or restart server."
            );
          }
          setPlans(mapped);
        }
      } catch {
        if (!cancelled) setPlans(DEFAULT_VOICE_CREDIT_PLANS);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      setLoading(false);
    };
  }, [enabled]);

  return { plans, loading };
}
